import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import Anchor from 'components/Anchor';

import useExclusiveOffer from 'hooks/useExclusiveOffer';

import { sendEvent } from 'lib/gtag';
import { toOfferLink } from 'lib/links';
import trackEvent from 'lib/posthog/trackEvent';
import { toFormattedGaEventActionPrefix } from 'lib/string';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import CardContent from './CardContent';
import CardWrapper from './CardWrapper';

import OFFER from 'constants/offer';

const OfferGridCard = ({
  authors = [],
  cardTitleAs = 'span',
  isOffersShowcaseCard = false,
  offerComments,
  offerImagePriority = false,
  position = 0,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerCoupon = null,
  storeDomain,
  userId,
  userName,
  userPhoto,
  gaEventActionPrefix = null,
  gaEventCategory = null,
  offerId,
  offerSlug,
  offerUserVisibility = OFFER.VISIBILITY_STATUS.ALL,
  onImageClick = noop,
  onTitleClick = noop,
  className,
}) => {
  const {
    isOfferBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveOffer,
  } = useExclusiveOffer();
  const { isLoggedIn } = useUser();

  const onClick = (event) => {
    sendEvent({
      action: formatGaEventActionByVisibility(
        'click__offer',
        offerUserVisibility
      ),
      category: gaEventCategory,
      logged: isLoggedIn,
    });

    if (gaEventCategory === 'timeline') {
      trackEvent(
        'click home offer card',
        { offer_id: offerId, position, surface: 'feed' },
        { destination: 'posthog' }
      );
    }

    if (!isOfferBlocked(offerUserVisibility, offerSlug)) {
      return;
    }

    event.preventDefault();
    onUnloggedUserTriesOpenExclusiveOffer(offerSlug, offerUserVisibility);
  };

  const onImageClickHandler = useCallback(
    (e) => {
      if (gaEventCategory) {
        sendEvent({
          category: gaEventCategory,
          action: `${toFormattedGaEventActionPrefix(
            gaEventActionPrefix
          )}product_image`,
          label: offerId,
        });
      }

      onImageClick(e);
    },
    [gaEventCategory, gaEventActionPrefix]
  );

  const onTitleClickHandler = useCallback(
    (e) => {
      if (gaEventCategory) {
        sendEvent({
          category: gaEventCategory,
          action: `${toFormattedGaEventActionPrefix(
            gaEventActionPrefix
          )}product_title`,
          label: offerId,
        });
      }

      onTitleClick(e);
    },
    [gaEventCategory, gaEventActionPrefix]
  );

  const isOfferExclusiveForApp =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.APP_ONLY;
  const isOfferExclusiveForLoggedUsers =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.LOGGED_ONLY;

  if (isOffersShowcaseCard) {
    return (
      <CardWrapper
        className={className}
        gaEventCategory={gaEventCategory}
        offerCoupon={offerCoupon}
        offerId={offerId}
        offerSlug={offerSlug}
        offerUserVisibility={offerUserVisibility}
      >
        <CardContent
          authors={authors}
          cardTitleAs={cardTitleAs}
          gaEventCategory={gaEventCategory}
          isOfferExclusiveForApp={isOfferExclusiveForApp}
          isOfferExclusiveForLoggedUsers={isOfferExclusiveForLoggedUsers}
          isOffersShowcaseCard={isOffersShowcaseCard}
          offerComments={offerComments}
          offerId={offerId}
          offerImagePriority={offerImagePriority}
          offerLikes={offerLikes}
          offerOldPrice={offerOldPrice}
          offerPhoto={offerPhoto}
          offerPrice={offerPrice}
          offerPriceType={offerPriceType}
          offerPublished={offerPublished}
          offerStatusName={offerStatusName}
          offerTags={offerTags}
          offerTitle={offerTitle}
          offerUserVisibility={offerUserVisibility}
          storeDomain={storeDomain}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
          onImageClickHandler={onImageClickHandler}
          onTitleClickHandler={onTitleClickHandler}
        />
      </CardWrapper>
    );
  }

  return (
    <NextLink href={toOfferLink(offerSlug)} prefetch={false} passHref>
      <Anchor
        className={twMerge(
          'relative flex h-full select-none flex-col justify-between rounded-4 border border-neutral-high-200 bg-neutral-high-100 p-4 shadow-sm transition-shadow hover:shadow-md dark:border-neutral-low-400 dark:bg-neutral-low-400 lg:p-6 lg:pb-3 lg:hover:ease-in',
          className
        )}
        onClick={onClick}
      >
        <CardContent
          authors={authors}
          cardTitleAs={cardTitleAs}
          gaEventCategory={gaEventCategory}
          isOfferExclusiveForApp={isOfferExclusiveForApp}
          isOfferExclusiveForLoggedUsers={isOfferExclusiveForLoggedUsers}
          isOffersShowcaseCard={isOffersShowcaseCard}
          offerComments={offerComments}
          offerId={offerId}
          offerImagePriority={offerImagePriority}
          offerLikes={offerLikes}
          offerOldPrice={offerOldPrice}
          offerPhoto={offerPhoto}
          offerPrice={offerPrice}
          offerPriceType={offerPriceType}
          offerPublished={offerPublished}
          offerStatusName={offerStatusName}
          offerTags={offerTags}
          offerTitle={offerTitle}
          offerUserVisibility={offerUserVisibility}
          storeDomain={storeDomain}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
          onImageClickHandler={onImageClickHandler}
          onTitleClickHandler={onTitleClickHandler}
        />
      </Anchor>
    </NextLink>
  );
};

OfferGridCard.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  cardTitleAs: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  gaEventActionPrefix: PropTypes.string,
  isOffersShowcaseCard: PropTypes.bool,
  offerId: PropTypes.number.isRequired,
  offerLikes: PropTypes.number.isRequired,
  position: PropTypes.number,
  offerComments: PropTypes.number.isRequired,
  offerIsHighlight: PropTypes.bool,
  offerImagePriority: PropTypes.bool,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
  userUsername: PropTypes.string,
  onImageClick: PropTypes.func,
  onTitleClick: PropTypes.func,
};

export default OfferGridCard;
